import { FormEvent } from 'react'
import { Head, Link, useForm } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout'
import PageTitle from '@/components/PageTitle'
import { Button } from '@/components/ui/Button'
import CardForm, { type CardFormData, type CardOptions } from '@/components/cards/CardForm'

interface CardRecord extends Partial<CardFormData> {
  id: string
  cr_name: string
  concept_image_url?: string | null
  design_image_url?: string | null
  cr_image_url?: string | null
  wiki_link?: string
}

interface CardPermissions {
  update: boolean
  write: boolean
  art: boolean
}

function toFormData(card: CardRecord): CardFormData {
  const s = (v: unknown) => (v == null ? '' : String(v))
  return {
    cr_name: card.cr_name,
    name: s(card.name),
    description: s(card.description),
    count: card.count ?? 1,
    arena: card.arena ?? 0,
    rarity: s(card.rarity) || 'common',
    card_type: s(card.card_type) || 'troop',
    transport: s(card.transport),
    target_air: card.target_air ?? false,
    target_ground: card.target_ground ?? false,
    focus: s(card.focus) || 'units',
    range_tiles: s(card.range_tiles),
    speed: s(card.speed),
    hit_speed: s(card.hit_speed),
    first_hit_speed: s(card.first_hit_speed),
    cost: card.cost ?? 0,
    form_size: s(card.form_size),
    concept_image: null,
    design_image: null,
    cr_image: null,
    wiki_link: s(card.wiki_link),
  }
}

export default function CardsEdit({ card, options, permissions }: { card: CardRecord; options: CardOptions; permissions: CardPermissions }) {
  const form = useForm<CardFormData>(toFormData(card))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({
      _method: 'put',
      card: data,
    }))
    form.post(`/cards/${card.id}`, { forceFormData: true })
  }

  return (
    <AppLayout header={<PageTitle title="Edit Card" subtitle={card.cr_name} />}>
      <Head title={`Edit ${card.name || card.cr_name}`} />
      <form onSubmit={submit} className="max-w-5xl space-y-6">
        <CardForm
          data={form.data}
          errors={form.errors}
          setData={form.setData}
          options={options}
          conceptImageUrl={card.concept_image_url}
          designImageUrl={card.design_image_url}
          crImageUrl={card.cr_image_url}
          metadataDisabled={!permissions.write}
          crImageDisabled={!permissions.write}
          artImageDisabled={!permissions.write && !permissions.art}
        />
        <div className="flex gap-2">
          <Button type="submit" disabled={form.processing}>
            {form.processing ? 'Saving…' : permissions.write ? 'Save changes' : 'Save art'}
          </Button>
          <Button asChild variant="outline" type="button">
            <Link href="/cards">Cancel</Link>
          </Button>
        </div>
      </form>
    </AppLayout>
  )
}
